import { DateTime } from 'luxon';
import invariant from 'tiny-invariant';
import { toSentence } from '@src/utils/stringUtils';
import {
  ChatActorType,
  ChatMessageType,
  ChatEventItemType,
  ChatTimelineItem,
  ChatEventTypeEnum,
  ChatEventChangeTypeEnum,
} from './types';

// Group chats can only include supervisees from the same organization (or all B2C).
export function filterAvailableGroupChatParticipants({
  potentialParticipants,
  selectedParticipants,
}: {
  potentialParticipants: ChatActorType[];
  selectedParticipants: ChatActorType[];
}): ChatActorType[] {
  const selectedIds = new Set(selectedParticipants.map((p) => p.id));
  const firstSelectedSupervisee = selectedParticipants.find((p) => p.type === 'user');
  const selectedOrganizationId = firstSelectedSupervisee?.organization?.id ?? null;

  return potentialParticipants.filter((participant) => {
    if (participant.type !== 'user') return false;
    if (selectedIds.has(participant.id)) return false;
    const participantOrganizationId = participant.organization?.id ?? null;
    return selectedOrganizationId === participantOrganizationId;
  });
}

const GROUPED_MESSAGE_THRESHOLD_IN_MINUTES = 3;

export type MessagesGroupedByTime = {
  time: DateTime;
  formattedTime: string;
  messages: ChatMessageType[];
  sender: ChatActorType;
};

export type TimelineEventItem = {
  type: 'event';
  time: DateTime;
  formattedTime: string;
  event: ChatEventItemType;
};

export type TimelineMessageGroup = {
  type: 'messages';
  time: DateTime;
  formattedTime: string;
  messages: ChatMessageType[];
  sender: ChatActorType;
};

export type TimelineItemByTime = TimelineEventItem | TimelineMessageGroup;

export type TimelineGroupedByDate = {
  date: DateTime;
  formattedDate: string;
  items: TimelineItemByTime[];
};

export type MessagesGroupedByDate = {
  date: DateTime;
  formattedDate: string;
  messagesByTime: MessagesGroupedByTime[];
};

export function addMessageToGroupedMessages(
  message: ChatMessageType,
  groupedMessages: MessagesGroupedByDate[],
  senderMap: Map<number, ChatActorType>,
) {
  const messageDateTime = DateTime.fromISO(message.createdAt);
  const lastGroupedByDateMessages = groupedMessages[groupedMessages.length - 1];
  if (lastGroupedByDateMessages?.date.hasSame(messageDateTime, 'day')) {
    const lastGroupedByTimeMessages =
      lastGroupedByDateMessages.messagesByTime[lastGroupedByDateMessages.messagesByTime.length - 1];

    if (
      messageDateTime.diff(lastGroupedByTimeMessages.time, 'minute').minutes <=
        GROUPED_MESSAGE_THRESHOLD_IN_MINUTES &&
      lastGroupedByTimeMessages.sender.id === message.senderActorId
    ) {
      lastGroupedByTimeMessages.messages.push(message);
    } else {
      const sender = senderMap.get(message.senderActorId);
      invariant(sender, `Sender with actorId ${message.senderActorId} not found`);
      lastGroupedByDateMessages.messagesByTime.push({
        time: messageDateTime,
        formattedTime: messageDateTime.toLocaleString(DateTime.TIME_SIMPLE),
        messages: [message],
        sender,
      });
    }
    return;
  }

  const sender = senderMap.get(message.senderActorId);
  invariant(sender, `Sender with actorId ${message.senderActorId} not found`);
  groupedMessages.push({
    date: messageDateTime,
    formattedDate: messageDateTime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
    messagesByTime: [
      {
        time: messageDateTime,
        messages: [message],
        formattedTime: messageDateTime.toLocaleString(DateTime.TIME_SIMPLE),
        sender,
      },
    ],
  });
}

export function addTimelineItemToGroupedTimeline(
  item: ChatTimelineItem,
  groupedTimeline: TimelineGroupedByDate[],
  participantsMap: Map<number, ChatActorType>,
) {
  const itemDateTime = DateTime.fromISO(item.createdAt);
  const lastGroupedByDate = groupedTimeline[groupedTimeline.length - 1];

  // Handle events - they always create a new item (don't group with messages)
  if (item.__typename === 'ChatEvent') {
    const eventItem: TimelineEventItem = {
      type: 'event',
      time: itemDateTime,
      formattedTime: itemDateTime.toLocaleString(DateTime.TIME_SIMPLE),
      event: item,
    };

    if (lastGroupedByDate?.date.hasSame(itemDateTime, 'day')) {
      lastGroupedByDate.items.push(eventItem);
    } else {
      groupedTimeline.push({
        date: itemDateTime,
        formattedDate: itemDateTime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
        items: [eventItem],
      });
    }
    return;
  }

  // Handle messages - can be grouped with other messages from the same sender
  const message = item;
  if (lastGroupedByDate?.date.hasSame(itemDateTime, 'day')) {
    const lastItem = lastGroupedByDate.items[lastGroupedByDate.items.length - 1];

    // Only group with previous message group if same sender and within threshold
    if (
      lastItem?.type === 'messages' &&
      itemDateTime.diff(lastItem.time, 'minute').minutes <= GROUPED_MESSAGE_THRESHOLD_IN_MINUTES &&
      lastItem.sender.id === message.senderActorId
    ) {
      lastItem.messages.push(message);
    } else {
      const sender = participantsMap.get(message.senderActorId);
      invariant(sender, `Sender with actorId ${message.senderActorId} not found`);
      lastGroupedByDate.items.push({
        type: 'messages',
        time: itemDateTime,
        formattedTime: itemDateTime.toLocaleString(DateTime.TIME_SIMPLE),
        messages: [message],
        sender,
      });
    }
    return;
  }

  const sender = participantsMap.get(message.senderActorId);
  invariant(sender, `Sender with actorId ${message.senderActorId} not found`);
  groupedTimeline.push({
    date: itemDateTime,
    formattedDate: itemDateTime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
    items: [
      {
        type: 'messages',
        time: itemDateTime,
        formattedTime: itemDateTime.toLocaleString(DateTime.TIME_SIMPLE),
        messages: [message],
        sender,
      },
    ],
  });
}

export function formatEventDescription(event: ChatEventItemType): string {
  switch (event.chatEventTypeId) {
    case ChatEventTypeEnum.PARTICIPANTS_CHANGED: {
      const added = event.targets.filter(
        (t) => t.chatEventChangeTypeId === ChatEventChangeTypeEnum.ADDED,
      );
      const removed = event.targets.filter(
        (t) => t.chatEventChangeTypeId === ChatEventChangeTypeEnum.REMOVED,
      );
      const reactivated = event.targets.filter(
        (t) => t.chatEventChangeTypeId === ChatEventChangeTypeEnum.REACTIVATED,
      );

      const parts: string[] = [];

      if (added.length > 0) {
        const names = toSentence(added.map((t) => t.targetActor.fullName));
        const verb = added.length === 1 ? 'was' : 'were';
        parts.push(`${names} ${verb} added to the chat`);
      }

      if (removed.length > 0) {
        const names = toSentence(removed.map((t) => t.targetActor.fullName));
        const verb = removed.length === 1 ? 'was' : 'were';
        parts.push(`${names} ${verb} removed from the chat`);
      }

      if (reactivated.length > 0) {
        const names = toSentence(reactivated.map((t) => t.targetActor.fullName));
        const verb = reactivated.length === 1 ? 'was' : 'were';
        parts.push(`${names} ${verb} re-added to the chat`);
      }

      return parts.join('. ');
    }
    case ChatEventTypeEnum.CHAT_ARCHIVED:
      return 'The chat was archived';
    case ChatEventTypeEnum.CHAT_UNARCHIVED:
      return 'The chat was unarchived';
    default:
      return '';
  }
}
